import { GROUND_ROW, TIE_STEP, frameId, propSortRow, type AssetManifest, type CellRect, type ChunkLandmark, type CompiledChunk, type ManifestObjectAppearance } from "daedalus-core";
import type { PictureProp } from "daedalus-renderer-phaser";
import { SIXTEENTHS, thingPlace, type MapPlace } from "odyssey-shared";
import { chunkKey } from "../chunk-key.ts";

/**
 * The things the map's pictures draw (ADR-100 decisions 54, 55, 58 and 59): the landmarks a client chunk lists and the
 * art of the shrines this character has discovered, put through one pure function into the props the picture painter
 * draws, so the Map window's feed and the minimap's cannot differ again. A landmark is placed by `thingPlace`, the one
 * function the world draws a live thing by, with the frame and mirroring of its appearance's initial state: a gate is
 * drawn closed, a well lowered, a roof whole.
 */

/**
 * What a landmark is placed against: the asset manifest, the world version's appearance table, and the map's tile and
 * chunk sizes.
 */
export interface LandmarkWorld {
  readonly manifest: AssetManifest;
  readonly appearances: readonly ManifestObjectAppearance[];
  readonly tileSize: number;
  readonly chunkSize: number;
}

/**
 * A count of the landmarks a picture skipped for an appearance past the table or a frame the manifest lacks, as the
 * painter counts a prop whose frame it lacks.
 */
export interface LandmarkTally {
  skipped: number;
}

/**
 * A prop for the painter with the key it sorts by.
 */
export interface KeyedProp {
  readonly prop: PictureProp;
  readonly key: number;
}

/**
 * A landmark placed: the compiled index of the layer it draws at, the prop the painter draws, its sort key and the
 * cells its art covers.
 */
export interface PlacedLandmark extends KeyedProp {
  readonly layer: number;
  readonly box: CellRect;
}

/**
 * Whether a prop's art covers any cell of a chunk. A prop's anchor is the bottom left cell of its art, which stands up
 * and to the right of it; a thing's anchor can fall between cells.
 *
 * @param x The anchor column.
 * @param y The anchor row.
 * @param width The frame's width in pixels.
 * @param height The frame's height in pixels.
 * @param tileSize Tile size in pixels.
 * @param left The chunk's first column.
 * @param top The chunk's first row.
 * @param size The chunk's side in tiles.
 * @returns True when the art overlaps the chunk.
 */
export function propReaches(x: number, y: number, width: number, height: number, tileSize: number, left: number, top: number, size: number): boolean {
  return x + Math.ceil(width / tileSize) > left && x < left + size && y + 1 > top && y + 1 - Math.ceil(height / tileSize) < top + size;
}

/**
 * Places one landmark: the frame of its appearance's initial state, mirrored as the appearance says, at the place
 * `thingPlace` gives its feet point, with the key a prop of that place sorts by.
 *
 * @param world What the landmark is placed against.
 * @param band The Players band, the compiled index of its lowest layer.
 * @param landmark The landmark.
 * @returns The placed landmark, or null for an appearance past the table, one with no initial state, or a frame the
 *   manifest lacks.
 */
export function placeLandmark(world: LandmarkWorld, band: number, landmark: ChunkLandmark): PlacedLandmark | null {
  const look = world.appearances[landmark.appearance];
  const shown = look?.states[look.initial];
  if (look === undefined || shown === undefined) {
    return null;
  }
  const id = frameId(shown.frame.tileset, null, shown.frame.tile);
  const rect = world.manifest.frames[id];
  if (rect === undefined) {
    return null;
  }
  const { tileSize } = world;
  const place = thingPlace(look, rect.w, landmark, tileSize, band);
  const row = rect.ground === true ? GROUND_ROW : propSortRow({ id, frame: shown.frame, x: place.x, y: place.y, flipX: look.flipX, flipY: look.flipY, sortPixels: place.sortPixels }, rect, tileSize);
  const wide = Math.ceil(rect.w / tileSize);
  const high = Math.ceil(rect.h / tileSize);
  return {
    layer: place.layer,
    prop: { frame: id, x: place.x, y: place.y, flipX: look.flipX, flipY: look.flipY },
    key: row + place.order * TIE_STEP,
    box: { x0: Math.floor(place.x), y0: Math.floor(place.y + 1 - high), x1: Math.ceil(place.x + wide), y1: Math.ceil(place.y + 1) },
  };
}

/**
 * The shrine art a character has discovered, as the pictures draw it (ADR-100 decision 58): replaced whole whenever a
 * `mapPlaces` arrives, listed by the chunk of each shrine's feet.
 *
 * @example
 * ```ts
 * const art = new ShrineArt(16);
 * const boxes = art.replace(places.places, world);
 * ```
 */
export class ShrineArt {
  private held: readonly ChunkLandmark[] = [];
  private byChunk = new Map<string, ChunkLandmark[]>();

  /**
   * @param chunkSize The map's chunk size in tiles.
   */
  public constructor(private readonly chunkSize: number) {}

  /**
   * The art held, one landmark for each place that carries `art`, in the order the places came.
   *
   * @returns The landmarks.
   */
  public get list(): readonly ChunkLandmark[] {
    return this.held;
  }

  /**
   * The art whose feet fall in a chunk.
   *
   * @param cx Chunk column.
   * @param cy Chunk row.
   * @returns The landmarks, none for a chunk with no art.
   */
  public in(cx: number, cy: number): readonly ChunkLandmark[] {
    return this.byChunk.get(chunkKey(cx, cy)) ?? [];
  }

  /**
   * Replaces the art with what the places carry.
   *
   * @param places The places of a `mapPlaces`.
   * @param world What the art is placed against, for the cells it covers.
   * @returns The cells the art that left and the art that arrived cover, each rectangle the art's own box in tiles, none
   *   for art that is unchanged or has no place to draw.
   */
  public replace(places: readonly MapPlace[], world: LandmarkWorld): readonly CellRect[] {
    const next: ChunkLandmark[] = [];
    for (const place of places) {
      if (place.art !== undefined) {
        next.push({ appearance: place.art.appearance, x: Math.round(place.art.x * SIXTEENTHS), y: Math.round(place.art.y * SIXTEENTHS) });
      }
    }
    const same = (a: ChunkLandmark, b: ChunkLandmark): boolean => a.appearance === b.appearance && a.x === b.x && a.y === b.y;
    const boxes: CellRect[] = [];
    for (const [from, to] of [[this.held, next], [next, this.held]] as const) {
      for (const landmark of from) {
        if (to.some((other) => same(landmark, other))) {
          continue;
        }
        const placed = placeLandmark(world, 0, landmark);
        if (placed !== null) {
          boxes.push(placed.box);
        }
      }
    }
    this.held = next;
    this.byChunk = new Map();
    for (const landmark of next) {
      const key = chunkKey(Math.floor(landmark.x / SIXTEENTHS / this.chunkSize), Math.floor(landmark.y / SIXTEENTHS / this.chunkSize));
      const list = this.byChunk.get(key);
      if (list === undefined) {
        this.byChunk.set(key, [landmark]);
      } else {
        list.push(landmark);
      }
    }
    return boxes;
  }
}

/**
 * The landmarks that reach into a chunk, found in the chunk and the eight beside it (a thing's art is centred on its
 * feet and rises above them, so it can reach the chunk on either side and the one above), placed and sorted by the key
 * a prop of their layer sorts by.
 *
 * @param world What the landmarks are placed against.
 * @param chunks The map's client chunks by `cx,cy`.
 * @param art The shrine art the character holds, or null for none.
 * @param band The Players band of the chunk, for a landmark whose appearance names no layer.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param tally Counts a landmark of this chunk that cannot be placed, once for each time the chunk is composed.
 * @returns The landmarks by the compiled index of the layer they draw at, each layer's in drawn order.
 */
export function landmarkProps(world: LandmarkWorld, chunks: ReadonlyMap<string, CompiledChunk>, art: ShrineArt | null, band: number, cx: number, cy: number, tally?: LandmarkTally): Map<number, KeyedProp[]> {
  const { tileSize, chunkSize, manifest } = world;
  const left = cx * chunkSize;
  const top = cy * chunkSize;
  const found = new Map<number, KeyedProp[]>();
  for (let ny = cy - 1; ny <= cy + 1; ny += 1) {
    for (let nx = cx - 1; nx <= cx + 1; nx += 1) {
      const listed = chunks.get(chunkKey(nx, ny))?.landmarks ?? [];
      const drawn = art?.in(nx, ny) ?? [];
      for (const landmark of drawn.length === 0 ? listed : [...listed, ...drawn]) {
        const placed = placeLandmark(world, band, landmark);
        if (placed === null) {
          if (nx === cx && ny === cy && tally !== undefined) {
            tally.skipped += 1;
          }
          continue;
        }
        const rect = manifest.frames[placed.prop.frame];
        if (rect === undefined || !propReaches(placed.prop.x, placed.prop.y, rect.w, rect.h, tileSize, left, top, chunkSize)) {
          continue;
        }
        const layer = found.get(placed.layer);
        if (layer === undefined) {
          found.set(placed.layer, [placed]);
        } else {
          layer.push(placed);
        }
      }
    }
  }
  for (const list of found.values()) {
    list.sort((a, b) => a.key - b.key);
  }
  return found;
}
